import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Clapperboard,
  Edit3,
  Film,
  Play,
  RefreshCw,
  Subtitles,
} from 'lucide-react';
import GenerationOverlay from '../components/GenerationOverlay';
import {
  cloneDemoShots,
  DEMO_PROJECT_TITLE,
  DEMO_SCRIPT,
  DEMO_VIDEO_URL,
  demoDuration,
} from '../demo/mockProject';

const pipeline = ['剧本解析', '分镜图生成', '视频 Snap', '字幕与剪辑', '完整成片'];

function DemoShowcasePage({ onNavigate }) {
  const shots = useMemo(() => cloneDemoShots(), []);
  const [isGenerating, setIsGenerating] = useState(true);
  const [activeSnap, setActiveSnap] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const snapVideoRef = useRef(null);
  const activeShot = shots[activeSnap];

  const segment = useMemo(() => {
    const duration = videoDuration > 0 ? videoDuration / shots.length : 2;
    return { start: duration * activeSnap, end: duration * (activeSnap + 1) };
  }, [activeSnap, shots.length, videoDuration]);

  useEffect(() => {
    const video = snapVideoRef.current;
    if (!video || !videoDuration) return;
    video.currentTime = segment.start;
    video.play().catch(() => {});
  }, [segment.start, videoDuration]);

  const editStoryboard = () => onNavigate('storyboard-result', {
    prompt: DEMO_SCRIPT,
    shots: cloneDemoShots(),
    workflow: 'video',
    projectTitle: DEMO_PROJECT_TITLE,
    fileName: 'Midnight_Circuit.fountain',
    demoMode: true,
  });

  return (
    <div className="min-h-full bg-[#090b0f] p-5 md:p-8">
      <div className="mx-auto max-w-[1500px]">
        <header className="mb-7 flex flex-col justify-between gap-5 xl:flex-row xl:items-end">
          <div className="flex items-start gap-4">
            <button onClick={() => onNavigate('home')} className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-gray-400 hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-400">
                <CheckCircle2 className="h-4 w-4" /> Full Pipeline Generated
              </div>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-white">{DEMO_PROJECT_TITLE}</h1>
              <p className="mt-2 text-sm text-gray-500">英文剧本 · 14 个分镜 · 14 个视频 Snap · 字幕 · 完整成片</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => setIsGenerating(true)} className="flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5">
              <RefreshCw className="h-4 w-4" /> 重播生成过程
            </button>
            <button onClick={editStoryboard} className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500">
              <Edit3 className="h-4 w-4" /> 进入分镜编辑
            </button>
          </div>
        </header>

        <div className="mb-7 overflow-x-auto rounded-2xl border border-emerald-400/15 bg-emerald-400/[0.035] p-4">
          <div className="flex min-w-[760px] items-center">
            {pipeline.map((step, index) => (
              <React.Fragment key={step}>
                <div className="flex items-center gap-2 text-sm text-gray-200">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-white"><Check className="h-4 w-4" /></span>
                  {step}
                </div>
                {index < pipeline.length - 1 && <div className="mx-4 h-px flex-1 bg-emerald-500/35" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <section className="rounded-3xl border border-white/10 bg-[#14171d] p-5 md:p-7">
          <div className="mb-5 flex flex-col justify-between gap-3 md:flex-row md:items-end">
            <div>
              <div className="flex items-center gap-2"><Clapperboard className="h-5 w-5 text-blue-400" /><h2 className="text-lg font-semibold text-white">01 · AI 分镜图</h2></div>
              <p className="mt-2 text-xs text-gray-500">以下 14 张画面全部来自“机车素材”文件夹，并按剧本镜头顺序展示。</p>
            </div>
            <span className="w-fit rounded-full bg-blue-400/10 px-3 py-1.5 text-xs text-blue-300">14 / 14 生成完成</span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            {shots.map((shot, index) => (
              <button key={shot.id} onClick={() => setActiveSnap(index)} className={`group overflow-hidden rounded-xl border text-left transition ${activeSnap === index ? 'border-blue-500 ring-2 ring-blue-500/15' : 'border-white/10 hover:border-white/25'}`}>
                <div className="relative aspect-[9/16] overflow-hidden bg-black">
                  <img src={shot.img} alt={`机车分镜 ${index + 1}`} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent" />
                  <span className="absolute left-2 top-2 rounded-md bg-black/55 px-1.5 py-1 text-[10px] text-white">{String(index + 1).padStart(2, '0')}</span>
                  <div className="absolute bottom-2 left-2 right-2"><p className="text-[10px] font-medium text-white">{shot.shotSize}</p><p className="mt-0.5 truncate text-[9px] text-gray-400">{shot.cameraMove}</p></div>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="mt-6 rounded-3xl border border-white/10 bg-[#14171d] p-5 md:p-7">
          <div className="mb-5 flex flex-col justify-between gap-3 md:flex-row md:items-end">
            <div>
              <div className="flex items-center gap-2"><Film className="h-5 w-5 text-violet-400" /><h2 className="text-lg font-semibold text-white">02 · 视频 Snap</h2></div>
              <p className="mt-2 text-xs text-gray-500">点击任意分镜，左侧会播放完整视频中对应的真实动态片段。</p>
            </div>
            <span className="w-fit rounded-full bg-violet-400/10 px-3 py-1.5 text-xs text-violet-300">Snap {String(activeSnap + 1).padStart(2, '0')} · Ready</span>
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(320px,0.62fr)_1fr]">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-black">
              <div className="relative mx-auto aspect-[9/16] max-h-[620px]">
                <video
                  ref={snapVideoRef}
                  src={DEMO_VIDEO_URL}
                  poster={activeShot.img}
                  muted
                  autoPlay
                  playsInline
                  controls
                  preload="metadata"
                  onLoadedMetadata={(event) => {
                    setVideoDuration(event.currentTarget.duration || 0);
                    event.currentTarget.currentTime = (event.currentTarget.duration / shots.length) * activeSnap;
                  }}
                  onTimeUpdate={(event) => {
                    if (event.currentTarget.currentTime >= segment.end) {
                      event.currentTarget.currentTime = segment.start;
                      event.currentTarget.play().catch(() => {});
                    }
                  }}
                  className="h-full w-full object-contain"
                />
                <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/65 px-3 py-1.5 text-[10px] font-medium text-white backdrop-blur">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" /> VIDEO SNAP {String(activeSnap + 1).padStart(2, '0')}
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl border border-white/10 bg-[#0e1015] p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-600">Active Shot</p>
                <h3 className="mt-2 text-xl font-semibold text-white">镜头 {String(activeSnap + 1).padStart(2, '0')} · {activeShot.shotSize}</h3>
                <p className="mt-3 text-sm leading-6 text-gray-400">{activeShot.desc}</p>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  {[
                    ['运镜', activeShot.cameraMove], ['光影', activeShot.lighting], ['情绪', activeShot.emotion], ['角色', activeShot.character],
                  ].map(([label, value]) => <div key={label} className="rounded-xl bg-white/[0.035] p-3"><p className="text-[10px] text-gray-600">{label}</p><p className="mt-1 text-xs text-gray-300">{value}</p></div>)}
                </div>
              </div>
              <div className="mt-4 grid grid-cols-4 gap-2 sm:grid-cols-7">
                {shots.map((shot, index) => (
                  <button key={shot.id} onClick={() => setActiveSnap(index)} className={`relative overflow-hidden rounded-lg border ${activeSnap === index ? 'border-violet-400' : 'border-white/10'}`}>
                    <img src={shot.img} alt={`Snap ${index + 1}`} className="aspect-[9/12] w-full object-cover" />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/25"><Play className="h-4 w-4 text-white" /></span>
                    <span className="absolute bottom-1 right-1 rounded bg-black/60 px-1 text-[8px] text-white">{index + 1}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mt-6 overflow-hidden rounded-3xl border border-emerald-400/20 bg-[#14171d]">
          <div className="flex flex-col justify-between gap-4 border-b border-white/10 p-5 md:flex-row md:items-center md:px-7">
            <div>
              <div className="flex items-center gap-2 text-emerald-400"><CheckCircle2 className="h-5 w-5" /><span className="text-sm font-semibold">03 · 完整成片生成完成</span></div>
              <h2 className="mt-2 text-xl font-semibold text-white">{DEMO_PROJECT_TITLE} · Final Cut</h2>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-white/5 px-3 py-2 text-xs text-gray-400"><Subtitles className="h-4 w-4" /> 字幕已添加 · 创世 First 1.0 Beta</div>
          </div>
          <div className="bg-black p-3 md:p-6"><video src={DEMO_VIDEO_URL} controls playsInline preload="metadata" className="mx-auto max-h-[75vh] w-full rounded-xl bg-black" /></div>
        </section>
      </div>

      {isGenerating && (
        <GenerationOverlay
          title="一键生成完整 AI 视频"
          subtitle="正在从英文剧本自动完成镜头解析、分镜渲染、视频 Snap 与成片剪辑。"
          duration={demoDuration(16000)}
          steps={['解析英文剧本与人物关系', '生成 14 张高清机车分镜', '生成逐镜视频 Snap', '添加字幕并智能剪辑', '输出完整高清视频']}
          onComplete={() => setIsGenerating(false)}
        />
      )}
    </div>
  );
}

export default DemoShowcasePage;
